import { useCallback, useMemo, useRef, useState } from "react";
import { validateEngineInput } from "../contracts/validate";
import type { EngineError, MaskRuleConfig } from "../contracts/types";
import type { AnonymizationResult } from "../domain/engine";
import { overrideFieldRule } from "../domain/engine";
import { runEngine } from "../worker/workerClient";
import type { EngineRunHandle } from "../worker/workerClient";
import { FIXTURES, getFixtureById } from "../fixtures";
import { loadRules, saveRules } from "../storage/localStore";
import { loadDatasetFile } from "../lib/fileInput";
import { LIMITS } from "../domain/limits";

export const MAX_INPUT_BYTES = LIMITS.maxUploadBytes;

export type InputState = "initial" | "edited" | "invalid" | "too-large";
export type RunState = "idle" | "processing" | "cancelled" | "completed" | "failed";

function byteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

function deriveInputState(text: string): { state: InputState; errors: EngineError[] } {
  if (byteLength(text) > MAX_INPUT_BYTES) {
    return {
      state: "too-large",
      errors: [
        {
          code: "LIMIT_EXCEEDED",
          message: `La entrada supera el límite de ${MAX_INPUT_BYTES / (1024 * 1024)} MB soportado en el navegador.`,
        },
      ],
    };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    return {
      state: "invalid",
      errors: [
        {
          code: "INPUT_INVALID",
          message: `JSON inválido: ${error instanceof Error ? error.message : "no se pudo interpretar."}`,
        },
      ],
    };
  }

  const validation = validateEngineInput(parsed);
  if (!validation.valid) {
    return { state: "invalid", errors: validation.errors };
  }

  return { state: "edited", errors: [] };
}

export function useEngine() {
  const initialFixture = FIXTURES[0]!;
  const [scenarioId, setScenarioId] = useState(initialFixture.id);
  const [inputText, setInputText] = useState(() => JSON.stringify(initialFixture.input, null, 2));
  const [inputState, setInputState] = useState<InputState>("initial");
  const [inputErrors, setInputErrors] = useState<EngineError[]>([]);
  const [runState, setRunState] = useState<RunState>("idle");
  const [engineResult, setEngineResult] = useState<AnonymizationResult | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const handleRef = useRef<EngineRunHandle | null>(null);

  const selectScenario = useCallback((id: string) => {
    const fixture = getFixtureById(id);
    if (!fixture) return;
    const text = JSON.stringify(fixture.input, null, 2);
    const derived = deriveInputState(text);
    setScenarioId(id);
    setInputText(text);
    setInputState(derived.state === "edited" ? "initial" : derived.state);
    setInputErrors(derived.errors);
    setEngineResult(null);
    setRunState("idle");
    setUploadError(null);
  }, []);

  const editInput = useCallback((text: string) => {
    setInputText(text);
    setEngineResult(null);
    setRunState("idle");
    const derived = deriveInputState(text);
    setInputState(derived.state);
    setInputErrors(derived.errors);
  }, []);

  const loadFile = useCallback(
    async (file: File) => {
      setUploadError(null);
      try {
        const loaded = await loadDatasetFile(file);
        const envelope = {
          schemaVersion: "1.0.0" as const,
          scenarioId: "archivo-local",
          payload: { records: loaded.records, rules: [] as MaskRuleConfig[] },
          options: { deterministic: true },
        };
        const text = JSON.stringify(envelope, null, 2);
        setScenarioId("archivo-local");
        setInputText(text);
        const derived = deriveInputState(text);
        setInputState(derived.state === "edited" ? "initial" : derived.state);
        setInputErrors(derived.errors);
        setEngineResult(null);
        setRunState("idle");
      } catch (error) {
        setUploadError(error instanceof Error ? error.message : "No se pudo leer el archivo.");
      }
    },
    [],
  );

  const run = useCallback(() => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(inputText);
    } catch {
      setInputState("invalid");
      return;
    }
    setRunState("processing");
    setEngineResult(null);
    const handle = runEngine(parsed);
    handleRef.current = handle;
    handle.result
      .then(async (result) => {
        let finalResult = result;
        try {
          const stored = await loadRules(result.output.evidence.scenarioId);
          if (stored && stored.rules.length > 0 && result.fieldOrder.length > 0) {
            let effectiveRules = result.effectiveRules;
            let maskedRecords = result.maskedRecords;
            let diff = result.diff;
            let coverage = result.coverage;
            for (const rule of stored.rules) {
              const outcome = overrideFieldRule(
                result.originalRecords,
                result.fieldOrder,
                result.fieldProfiles,
                effectiveRules,
                result.output.runId,
                rule.field,
                { strategy: rule.strategy, param: rule.param },
              );
              effectiveRules = outcome.effectiveRules;
              maskedRecords = outcome.maskedRecords;
              diff = outcome.diff;
              coverage = outcome.coverage;
            }
            finalResult = { ...result, effectiveRules, maskedRecords, diff, coverage };
          }
        } catch {
          // IndexedDB no disponible o vacío: se continúa con el resultado sin reglas guardadas.
        }
        setEngineResult(finalResult);
        setRunState(
          finalResult.output.status === "cancelled"
            ? "cancelled"
            : finalResult.output.status === "failed"
              ? "failed"
              : "completed",
        );
      })
      .catch((error: Error) => {
        setEngineResult(null);
        setRunState("failed");
        setInputErrors([{ code: "INTERNAL_ERROR", message: error.message }]);
      })
      .finally(() => {
        handleRef.current = null;
      });
  }, [inputText]);

  const cancel = useCallback(() => {
    handleRef.current?.cancel();
  }, []);

  const applyRuleOverride = useCallback((field: string, patch: Partial<Pick<MaskRuleConfig, "strategy" | "param">>) => {
    setEngineResult((previous) => {
      if (!previous) return previous;
      const outcome = overrideFieldRule(
        previous.originalRecords,
        previous.fieldOrder,
        previous.fieldProfiles,
        previous.effectiveRules,
        previous.output.runId,
        field,
        patch,
      );
      return { ...previous, ...outcome };
    });
  }, []);

  const saveCurrentRules = useCallback(async (): Promise<void> => {
    if (!engineResult) return;
    await saveRules({
      scenarioId: engineResult.output.evidence.scenarioId,
      updatedAt: new Date().toISOString(),
      rules: engineResult.effectiveRules,
    });
  }, [engineResult]);

  const fieldProfiles = useMemo(() => engineResult?.fieldProfiles ?? [], [engineResult]);

  return {
    scenarioId,
    inputText,
    inputState,
    inputErrors,
    runState,
    engineResult,
    fieldProfiles,
    uploadError,
    selectScenario,
    editInput,
    loadFile,
    run,
    cancel,
    applyRuleOverride,
    saveCurrentRules,
  };
}
